import { generateCodeChallenge, generateCodeVerifier, generateState } from '../lib/pkce'
import { AUTH_CLIENT_ID, AUTH_REDIRECT_URI, AUTH_SCOPE, AUTH_SERVER_URL } from '../config/env'
import { clearAuthData } from './authStorage'

const VERIFIER_KEY = 'pkce_verifier'
const STATE_KEY = 'pkce_state'

// Decodifica o payload de um JWT sem validar assinatura (so pra ler claims no
// client - a validacao real e feita pelo authserver ao emitir o token).
const decodeJwtPayload = (token) => {
	try {
		const payload = token.split('.')[1]
		const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
		return JSON.parse(json)
	} catch {
		return null
	}
}

const persistTokens = (data) => {
	localStorage.setItem('authToken', data.access_token)
	localStorage.setItem('tokenType', data.token_type || 'Bearer')

	if (data.refresh_token) {
		localStorage.setItem('refreshToken', data.refresh_token)
	}

	if (data.expires_in) {
		localStorage.setItem('tokenExpiration', (Date.now() + data.expires_in * 1000).toString())
	}

	const claims = decodeJwtPayload(data.id_token || data.access_token)
	localStorage.setItem('username', claims?.sub || claims?.preferred_username || 'Usuario')
}

// Fluxo OAuth2 Authorization Code + PKCE contra o authserver-api. Client publico
// "backoffice" (sem client_secret) - ver db.changelog-oauth-backoffice-seed.xml
// no authserver-api.
export async function redirectToLogin() {
	const verifier = generateCodeVerifier()
	const challenge = await generateCodeChallenge(verifier)
	const state = generateState()

	sessionStorage.setItem(VERIFIER_KEY, verifier)
	sessionStorage.setItem(STATE_KEY, state)

	const url = new URL('/oauth2/authorize', AUTH_SERVER_URL)
	url.searchParams.set('response_type', 'code')
	url.searchParams.set('client_id', AUTH_CLIENT_ID)
	url.searchParams.set('redirect_uri', AUTH_REDIRECT_URI)
	url.searchParams.set('scope', AUTH_SCOPE)
	url.searchParams.set('state', state)
	url.searchParams.set('code_challenge', challenge)
	url.searchParams.set('code_challenge_method', 'S256')

	window.location.assign(url.toString())
}

export async function handleLoginCallback(searchParams) {
	const error = searchParams.get('error')
	if (error) {
		throw new Error(searchParams.get('error_description') || error)
	}

	const code = searchParams.get('code')
	const state = searchParams.get('state')
	const expectedState = sessionStorage.getItem(STATE_KEY)
	const verifier = sessionStorage.getItem(VERIFIER_KEY)

	if (!code || !state || state !== expectedState || !verifier) {
		throw new Error('Falha na validacao do login. Tente novamente.')
	}

	const response = await fetch(new URL('/oauth2/token', AUTH_SERVER_URL), {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			grant_type: 'authorization_code',
			code,
			redirect_uri: AUTH_REDIRECT_URI,
			client_id: AUTH_CLIENT_ID,
			code_verifier: verifier,
		}),
	})

	sessionStorage.removeItem(VERIFIER_KEY)
	sessionStorage.removeItem(STATE_KEY)

	if (!response.ok) {
		throw new Error('Nao foi possivel concluir o login.')
	}

	persistTokens(await response.json())
}

export async function refreshAccessToken() {
	const refreshToken = localStorage.getItem('refreshToken')
	if (!refreshToken) {
		clearAuthData()
		return null
	}

	const response = await fetch(new URL('/oauth2/token', AUTH_SERVER_URL), {
		method: 'POST',
		headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
		body: new URLSearchParams({
			grant_type: 'refresh_token',
			refresh_token: refreshToken,
			client_id: AUTH_CLIENT_ID,
		}),
	})

	if (!response.ok) {
		clearAuthData()
		return null
	}

	const data = await response.json()
	persistTokens(data)
	return data.access_token
}

export function logout() {
	clearAuthData()
}
